import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { useCurrency } from "../../context/CurrencyContext";
import SubPageShell from "../../components/SubPageShell";

const ORDER_GROUPS = [
  { key: "all", t: "grp_all", match: null },
  { key: "unpaid", t: "grp_unpaid", match: ["unpaid", "pending_payment", "pending", "awaiting_payment"] },
  { key: "processing", t: "grp_processing", match: ["processing", "confirmed", "paid", "placed", "preparing"] },
  { key: "shipped", t: "grp_shipped", match: ["shipped", "dispatched", "in_transit", "on_the_way", "out_for_delivery"] },
  { key: "refunds", t: "grp_refunds", match: ["refund", "refunded", "return", "returned", "cancelled", "canceled", "disputed"] },
  { key: "review", t: "grp_review", match: ["delivered", "completed", "complete", "to_review", "review"] },
];
const groupOf = (o) => {
  const s = String(o.status || "").toLowerCase();
  const g = ORDER_GROUPS.find((x) => x.match && x.match.some((m) => s.includes(m)));
  return g ? g.key : "processing";
};

export default function AccountOrders() {
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [orders, setOrders] = useState(null);
  const active = params.get("status") || "all";

  useEffect(() => {
    if (user === undefined) return;
    if (!user) { navigate("/login?next=/account/orders"); return; }
    api("/orders/mine", { auth: true }).then(setOrders).catch(() => setOrders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;
  const list = (orders || []).filter((o) => active === "all" || groupOf(o) === active);

  return (
    <SubPageShell title={t("tab_orders")} tid="account-orders-page">
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {ORDER_GROUPS.map((g) => (
          <button
            key={g.key}
            data-testid={`orders-filter-${g.key}`}
            onClick={() => setParams(g.key === "all" ? {} : { status: g.key })}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${
              active === g.key ? "bg-teal-700 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-teal-400"
            }`}
          >
            {t(g.t)}
          </button>
        ))}
      </div>

      {orders === null ? (
        <p data-testid="orders-loading" className="py-10 text-center text-sm text-slate-500">...</p>
      ) : list.length === 0 ? (
        <div data-testid="orders-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 py-12 text-center">
          <p className="font-heading font-semibold text-slate-800">{t("no_orders")}</p>
          <p className="mt-1 text-sm text-slate-500">{t("no_orders_sub")}</p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {list.map((o) => (
            <Link
              key={o.id || o.order_id}
              data-testid={`order-row-${o.id || o.order_id}`}
              to={`/orders/${o.id || o.order_id}`}
              className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300"
            >
              <span className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-teal-50">
                {(o.product_image || o.image) && (
                  <img src={mediaUrl(o.product_image || o.image)} alt="" loading="lazy" className="h-full w-full object-cover" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {o.product_title || o.title || `${t("order_t")} #${String(o.id || o.order_id).slice(-8).toUpperCase()}`}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {o.quantity ? `${o.quantity} pcs · ` : ""}{o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {o.total != null && <p className="text-sm font-extrabold text-teal-700">{fmt(o.total)}</p>}
                {o.status && (
                  <span className="mt-1 inline-block rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold capitalize text-teal-800">
                    {o.status}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </SubPageShell>
  );
}
